import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { GitMerge, Building2, Eye, ShieldAlert, User, Check, FolderOpen } from 'lucide-react';
import useFusionPtrStore, { MAX_FUSION_ANALYSES } from '../../stores/useFusionPtrStore';
import { summarizeSnapshot } from './SnapshotSections';
import { formatUpdatedAgo } from './fusionFormat';

export { formatUpdatedAgo };

const classificationColors = {
  Confidentielle: 'bg-red-100 text-red-700',
  Restreinte: 'bg-amber-100 text-amber-700',
  Sensible: 'bg-blue-100 text-blue-700',
  'Non protégée': 'bg-slate-100 text-slate-600',
};

// Unified analysis cards: the entry point for both fusion selection and card details.
const FusionAnalysisSelector = ({ disabled = false, onStartFusion, canCollect = true, onOpenDetails }) => {
  const { t } = useTranslation();
  const analyses = useFusionPtrStore((s) => s.analyses);
  const selectedIds = useFusionPtrStore((s) => s.selectedIds);
  const toggleSelect = useFusionPtrStore((s) => s.toggleSelect);
  const snapshotCache = useFusionPtrStore((s) => s.snapshotCache);

  if (!analyses.length) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-10 text-center">
        <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-400">
          <FolderOpen size={20} />
        </div>
        <p className="text-sm text-slate-500">
          {t('fusionPtr.noAnalyses', 'Aucune analyse disponible pour la fusion.')}
        </p>
      </div>
    );
  }

  const toggleOnKey = (e, id) => {
    if (e.key === 'Enter' || (e.key === ' ' && e.target === e.currentTarget)) {
      e.preventDefault();
      toggleSelect(id);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1.5">
        <h3 className="text-sm font-bold text-slate-800">
          {t('fusionPtr.analysesTitle', 'Analyses (1 à 10)')}
        </h3>
        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-100 rounded-full px-2.5 py-0.5 tabular-nums">{selectedIds.length} / {MAX_FUSION_ANALYSES}</span>
          {canCollect && (
            <button
              type="button"
              disabled={!selectedIds.length || disabled}
              onClick={onStartFusion}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <GitMerge size={15} />
              {t('fusionPtr.startFusion', 'Lancer la fusion ({{count}})', { count: selectedIds.length })}
            </button>
          )}
        </div>
      </div>
      <p className="mb-4 text-xs text-slate-400">
        {t('fusionPtr.selectHint', 'Cliquez sur les cartes pour sélectionner les analyses à fusionner.')}
      </p>
      <div className="max-h-[480px] overflow-y-auto pr-1 custom-scrollbar">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {analyses.map((a) => {
          const id = String(a._id || a.id);
          const selected = selectedIds.includes(id);
          const snap = snapshotCache[id];
          const summary = snap ? summarizeSnapshot(snap) : null;
          return (
            <div
              key={id}
              role="button"
              tabIndex={disabled ? -1 : 0}
              aria-pressed={selected}
              onClick={() => { if (!disabled) toggleSelect(id); }}
              onKeyDown={(e) => { if (!disabled) toggleOnKey(e, id); }}
              title={t('fusionPtr.selectForFusion', 'Sélectionner pour la fusion')}
              className={`relative text-left bg-white rounded-xl border p-4 transition-all duration-200 outline-none focus-visible:ring-2 focus-visible:ring-blue-400 overflow-hidden ${
                selected
                  ? 'border-blue-500 ring-1 ring-blue-400 shadow-sm bg-blue-50/40'
                  : 'border-slate-200 hover:border-blue-300 hover:shadow-sm'
              } ${disabled ? 'opacity-60' : 'cursor-pointer'}`}
            >
              {selected && <span className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500" aria-hidden="true" />}
              <div className="flex items-start gap-2.5">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span aria-hidden="true" className={`mt-0.5 flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded-md border transition-colors ${selected ? 'border-blue-600 bg-blue-600 text-white' : 'border-slate-300 bg-white text-transparent'}`}>
                      <Check size={12} strokeWidth={3} />
                    </span>
                    <span className="text-[15px] font-semibold text-slate-800 truncate">{a.name}</span>
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={(e) => { e.stopPropagation(); if (!disabled) onOpenDetails(id); }}
                      title={t('fusionPtr.viewDetails', 'Voir le détail')}
                      aria-label={t('fusionPtr.details', 'Détails')}
                      className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium text-slate-400 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                    >
                      <Eye size={13} />
                      {t('fusionPtr.details', 'Détails')}
                    </button>
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600 max-w-[140px]">
                      <Building2 size={10} className="flex-shrink-0" />
                      <span className="truncate">{a.companyName || '—'}</span>
                    </span>
                    {a.classification && (
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${classificationColors[a.classification] || 'bg-slate-100 text-slate-600'}`}>
                        <ShieldAlert size={10} className="flex-shrink-0" />
                        {a.classification}
                      </span>
                    )}
                    {a.version && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-50 text-slate-500 border border-slate-100 tabular-nums">
                        v{a.version}
                      </span>
                    )}
                  </div>
                  {a.description ? (
                    <p className="mt-1.5 text-xs text-slate-600 leading-snug line-clamp-2">{a.description}</p>
                  ) : (
                    <p className="mt-1.5 text-xs text-slate-400 italic">
                      {t('fusionPtr.noDescription', 'Aucune description')}
                    </p>
                  )}
                  <div className="mt-2 pt-2 border-t border-slate-100 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
                    {a.createdByName && (
                      <span className="inline-flex items-center gap-1 min-w-0">
                        <User size={10} className="flex-shrink-0" />
                        <span className="truncate">{t('fusionPtr.createdBy', 'Par {{name}}', { name: a.createdByName })}</span>
                      </span>
                    )}
                    {a.updatedAt && (
                      <span className="tabular-nums">{formatUpdatedAgo(t, a.updatedAt)}</span>
                    )}
                    {summary && (
                      <span className="ml-auto font-medium text-slate-600 tabular-nums">
                        {t('fusionPtr.measuresCount', '{{count}} mesures', { count: summary.measuresCount })}
                        {' · '}
                        {t('fusionPtr.assetsCount', '{{count}} actifs', { count: summary.assetsCount })}
                        {' · '}
                        {t('fusionPtr.budgetTotal', 'Budget total : {{amount}} €', { amount: summary.budgetTotal.toLocaleString() })}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      </div>
    </div>
  );
};

FusionAnalysisSelector.propTypes = {
  disabled: PropTypes.bool,
  onStartFusion: PropTypes.func.isRequired,
  canCollect: PropTypes.bool,
  onOpenDetails: PropTypes.func.isRequired,
};

export default FusionAnalysisSelector;
